import { createTheme } from '@mui/material/styles';
import { componentOverrides } from './componentOverrides';

const typography = {
  fontFamily: '"Poppins", sans-serif',
  h1_fontFamily: '"Poppins", sans-serif',
  label_fontFamily: '"Poppins", sans-serif',
  // Responsive font sizes for better display on all screen resolutions
  h1: {
    fontSize: '2rem',      // 32px default
    '@media (min-width:600px)': {
      fontSize: '2.5rem',  // 40px on tablet+
    },
    '@media (min-width:900px)': {
      fontSize: '3rem',    // 48px on desktop
    },
    '@media (max-width:1024px)': {
      fontSize: '2.25rem', // 36px on small desktops (1024x768)
    },
  },
  h2: {
    fontSize: '1.5rem',    // 24px default
    '@media (min-width:600px)': {
      fontSize: '1.875rem', // 30px on tablet+
    },
    '@media (min-width:900px)': {
      fontSize: '2.25rem', // 36px on desktop
    },
    '@media (max-width:1024px)': {
      fontSize: '1.75rem', // 28px on small desktops
    },
  },
  h3: {
    fontSize: '1.25rem',   // 20px default
    '@media (min-width:600px)': {
      fontSize: '1.5rem',  // 24px on tablet+
    },
    '@media (min-width:900px)': {
      fontSize: '1.875rem', // 30px on desktop
    },
    '@media (max-width:1024px)': {
      fontSize: '1.375rem', // 22px on small desktops
    },
  },
  h4: {
    fontSize: '1.125rem',  // 18px default
    '@media (min-width:600px)': {
      fontSize: '1.25rem', // 20px on tablet+
    },
    '@media (min-width:900px)': {
      fontSize: '1.5rem',  // 24px on desktop
    },
    '@media (max-width:1024px)': {
      fontSize: '1.25rem', // 20px on small desktops
    },
  },
  h5: {
    fontSize: '1rem',      // 16px default
    '@media (min-width:600px)': {
      fontSize: '1.125rem', // 18px on tablet+
    },
    '@media (min-width:900px)': {
      fontSize: '1.25rem', // 20px on desktop
    },
    '@media (max-width:1024px)': {
      fontSize: '1.125rem', // 18px on small desktops
    },
  },
  h6: {
    fontSize: '0.875rem',  // 14px default
    '@media (min-width:600px)': {
      fontSize: '1rem',    // 16px on tablet+
    },
    '@media (min-width:900px)': {
      fontSize: '1.125rem', // 18px on desktop
    },
    '@media (max-width:1024px)': {
      fontSize: '1rem',    // 16px on small desktops
    },
  },
  body1: {
    fontSize: '0.875rem',  // 14px default
    '@media (min-width:600px)': {
      fontSize: '0.9375rem', // 15px on tablet+
    },
    '@media (min-width:900px)': {
      fontSize: '1rem',    // 16px on desktop
    },
    '@media (max-width:1024px)': {
      fontSize: '0.9375rem', // 15px on small desktops
    },
  },
  body2: {
    fontSize: '0.8125rem', // 13px default
    '@media (min-width:600px)': {
      fontSize: '0.875rem', // 14px on tablet+
    },
    '@media (min-width:900px)': {
      fontSize: '0.9375rem', // 15px on desktop
    },
    '@media (max-width:1024px)': {
      fontSize: '0.875rem', // 14px on small desktops
    },
  },
  subtitle1: {
    fontSize: '0.875rem',  // 14px default
    '@media (min-width:600px)': {
      fontSize: '0.9375rem', // 15px on tablet+
    },
    '@media (min-width:900px)': {
      fontSize: '1rem',    // 16px on desktop
    },
    '@media (max-width:1024px)': {
      fontSize: '0.9375rem', // 15px on small desktops
    },
  },
  subtitle2: {
    fontSize: '0.8125rem', // 13px default
    '@media (min-width:600px)': {
      fontSize: '0.875rem', // 14px on tablet+
    },
    '@media (min-width:900px)': {
      fontSize: '0.9375rem', // 15px on desktop
    },
    '@media (max-width:1024px)': {
      fontSize: '0.875rem', // 14px on small desktops
    },
  },
  button: {
    fontSize: '0.8125rem', // 13px default
    '@media (min-width:600px)': {
      fontSize: '0.875rem', // 14px on tablet+
    },
    '@media (min-width:900px)': {
      fontSize: '0.9375rem', // 15px on desktop
    },
    '@media (max-width:1024px)': {
      fontSize: '0.875rem', // 14px on small desktops
    },
  },
  caption: {
    fontSize: '0.75rem',   // 12px default
    '@media (min-width:900px)': {
      fontSize: '0.8125rem', // 13px on desktop
    },
    '@media (max-width:1024px)': {
      fontSize: '0.75rem', // 12px on small desktops
    },
  },
};

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0259a5ff',          // crisp blue
      light: '#64b5f6',
      dark: '#1769aa',
    },
    secondary: {
      main: '#00bcd4',          // cyan accent
      light: '#62efff',
      dark: '#008ba3',
    },
    background: {
      default: '#f4f8fb',       // very light blue-gray
      paper: '#ffffff',
    },
    text: {
      primary: '#111921ff',       // deep blue for contrast
      secondary: '#4f5b62',     // cool gray
    },
    sidebar: {
      background: '#ffffff',    // soft blue background
      text: '#6D94C5',          // bluish-gray
      textSecondary: '#6e8ca0',
      divider: '#e3eaf3',
      hover: '#eaf3fb',
      active: '#e3f2fd',
      menuItem: {
        background: 'transparent',
        text: '#6D94C5',
        textActive: '#0259a5ff',
        icon: '#6D94C5',
        iconActive: '#0259a5ff',
        backgroundActive: '#e3f2fd',
        backgroundHover: '#eaf3fb',
      },
    },
    topbar: {
      background: '#ffffff',
      text: '#234059',
      textSecondary: '#90caf9',
      divider: '#2196f3',
      hover: '#e3f2fd',
    },
    footer: {
      background: '#f6fafd',
      text: '#234059',
    },
  },
  typography,
  components: componentOverrides,
});

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#64b5f6',          // lighter blue
      light: '#b3e5fc',
      dark: '#2286c3',
    },
    secondary: {
      main: '#00bcd4',          // cyan accent
      light: '#4dd0e1',
      dark: '#008ba3',
    },
    background: {
      default: '#181f2a',       // deep blue-gray
      paper: '#222d3b',         // darker bluish
    },
    text: {
      primary: '#ffffff',
      secondary: '#b3c2d1',
    },
    sidebar: {
      background: '#222d3b',
      text: '#b3c2d1',
      textSecondary: '#7194b3',
      divider: '#2c3a4e',
      hover: '#233044',
      active: '#1769aa',
      menuItem: {
        background: 'transparent',
        text: '#7194b3',
        textActive: '#ffffff',
        icon: '#7194b3',
        iconActive: '#ffffff',
        backgroundActive: '#181f2b',
        backgroundHover: '#181f2b',
      },
    },
    topbar: {
      background: '#1a2332',
      text: '#b3c2d1',
      textSecondary: '#64b5f6',
      divider: '#2286c3',
      hover: '#233044',
    },
    footer: {
      background: '#222d3b',
      text: '#b3c2d1',
    },
  },
  typography,
  components: componentOverrides,
});